//* Vue
import type { Reactive } from "vue";
//* 组件库
import { utility, type UiTypes } from "various-ui";
//* 组件属性
import type { ModuleFormData, ModuleTranscript, ModuleCodonContrast, ModuleTranscriptContrast } from "./types";

let key = 0;
export const utils = {
    //* 数据初始化
    data: (response?: ModuleFormData, option?: { clear?: boolean }): ModuleFormData => {
        //* 数据初始化
        const data = {
            key: key++,
            id: "",
            ref: "",
            alt: "",
            name: "",
            species: "",
            visible: false,
            sequence: "",
            transcript: "",
            transcripts: <ModuleTranscript[]>[],
        };

        //* 是否使用数据缓存
        if (!option?.clear) {
            data.id = response?.id || "";
            data.name = response?.name || "";
            data.species = response?.species || "";
            data.visible = response?.visible || false;
            data.sequence = response?.sequence || "";
            data.transcript = response?.transcript || "";
            data.transcripts = response?.transcripts || [];
        }

        switch (response?.mode) {
            case "Transcript":
                return Object.assign(data, { mode: response.mode || "Transcript", data: { coordinate: {}, contrast: {} } });
            case "Protein":
                return Object.assign(data, { mode: response.mode || "Protein", data: { site: "", contrast: {} } });
            default: {
                return Object.assign(data, {
                    mode: response?.mode || "Paste",
                    data: { keyword: "" },
                });
            }
        }
    },

    //* 数据清空
    clear: (data: Reactive<ModuleFormData[]>, key: number) => {
        data[key].id = "";
        data[key].ref = "";
        data[key].alt = "";
        data[key].name = "";
        data[key].species = "";
        data[key].visible = false;
        data[key].sequence = "";
        data[key].transcript = "";
        data[key].transcripts = [];
        data[key].data.range = undefined;
        switch (data[key].mode) {
            case "Transcript": {
                data[key].data.coordinate = {};
                data[key].data.contrast = {};
                break;
            }
            case "Protein": {
                data[key].data.site = "";
                data[key].data.codons = undefined;
                data[key].data.contrast = {};
                break;
            }
            default: {
                data[key].data.mode = undefined;
                data[key].data.codons = undefined;
                data[key].data.keyword = "";
                break;
            }
        }
    },

    //* 碱基处理
    codon: (str: string) => {
        //* 数据更新
        const result = str?.toUpperCase()?.match(/[a|A|t|T|c|C|g|G]/g);
        if (!result?.length) return "";
        else {
            return result.slice(0, 100).join("");
        }
    },

    //* 数据解析处理
    analysis: (data: Reactive<ModuleFormData[]>, key: number, response?: any) => {
        //* 数据更新
        data[key].alt = utils.codon(response?.alt || "");
        data[key].ref = response?.ref || "";
        data[key].species = response?.species || "";
        data[key].sequence = response?.sequence || "";
        if (data[key].mode == "Paste" || data[key].mode == "Variant") {
            data[key].data.mode = response?.mode;
            data[key].data.range = { start: response?.range?.[0], end: response?.range?.[1] };
            data[key].data.codons = response?.codons || [];
        }

        if (data[key].mode == "Paste") {
            data[key].id = response?.id || "";
            data[key].name = response?.name || "";
            data[key].transcripts = utils.transcript(response?.transcripts);
            data[key].transcript = response?.transcript || "";
        }
    },

    //* reference蛋白质解析
    reference: (data: ModuleFormData) => {
        const codon = Contrast.Codon[data.ref];
        if (!codon) return "";
        else {
            return `${codon.name}(${codon.shortnanme}): ${data.ref}`;
        }
    },

    //* 转录本数据处理
    transcript: (data?: UiTypes.candidate[]) => {
        const result = data?.map((value) => {
            const label = `${value.value}${value.mane ? " [MANE]" : ""}`;
            return {
                cds: value.cds || [],
                name: label + (value.ensembl ? `-${value.ensembl}` : ""),
                label: label,
                value: value.value,
                exons: value.exons || [],
            };
        });

        return result || [];
    },

    //* 蛋白对照表生成
    codon_contrast: (data: ModuleFormData) => {
        //* 临时数据
        const config = { start: -1, codon: "", site: 1 };
        const result: { [name: number]: ModuleCodonContrast | undefined } = {};
        data.transcripts
            .find((value) => value.value == data.transcript)
            ?.cds?.forEach((value: number[]) => {
                for (let i = value[0]; i <= value[1]; i++) {
                    if (config.codon.length == 0) {
                        config.start = i;
                        config.codon = data.sequence.charAt(i - 1);
                    } else {
                        config.codon += data.sequence.charAt(i - 1);
                        if (config.codon.length >= 3) {
                            result[config.site] = Object.assign(utility.cloneDeep(config), { end: i, readonly: i - config.start >= 3 });
                            config.site += 1;
                            config.codon = "";
                        }
                    }
                }
            });

        return result;
    },

    //* 转录本对照表生成
    transcript_contrast: (data: ModuleFormData) => {
        const transcript = data.transcripts.find((value) => value.value == data.transcript);
        if (!transcript) return {};
        else {
            const sites: number[] = [];
            transcript.exons?.forEach((value: number[]) => {
                for (let i = value[0]; i <= value[1]; i++) {
                    sites.push(i);
                }
            });

            const result: { [name: number]: ModuleTranscriptContrast } = {};
            const config = { site: -sites.filter((value) => value < transcript.cds?.[0]?.[0]).length };
            transcript.exons?.forEach((value: number[]) => {
                for (let i = value[0]; i <= value[1]; i++) {
                    if (config.site == 0) config.site = 1;
                    result[config.site++] = { site: i, char: data.sequence.charAt(i - 1) };
                }
            });

            return result;
        }
    },
};
